import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory2';
import PeopleIcon from '@mui/icons-material/PeopleAlt';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import KeyIcon from '@mui/icons-material/VpnKey';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { colors, typo } from '../../theme/tokens';
import ResetCacheCard from './ResetCacheCard';

interface UpcomingSurface {
  title: string;
  description: string;
  icon: React.ComponentType<{ sx?: any }>;
  round: 2 | 3 | 6;
  /** R6: surfaces that have flipped from "upcoming" to "live" show a
   *  "Live" chip in place of the round chip. */
  live?: boolean;
}

/**
 * Upcoming surfaces — one row per admin feature the sidebar advertises.
 * Order mirrors the sidebar so a super-admin can scan "is my feature in
 * Round 2 or later?" without cross-referencing docs.
 */
const UPCOMING: UpcomingSurface[] = [
  {
    title: 'Global Catalog',
    description: '8 platform entities shared across tenants (tasks, adventures, sweepstakes, …).',
    icon: InventoryIcon,
    round: 2,
  },
  {
    title: 'Reference Catalogs',
    description: '10 lookup tables (task types, wallet types, component types, …).',
    icon: CategoryIcon,
    round: 2,
  },
  {
    title: 'Users',
    description: 'Super-admin grants and platform access.',
    icon: PeopleIcon,
    round: 6,
    live: true,
  },
  {
    title: 'Field Definitions',
    description: 'JSON schemas that drive dynamic form fields across tenant content.',
    icon: TableChartIcon,
    round: 3,
    live: true,
  },
  {
    title: 'Admin Scripts',
    description: 'Global scripts run by operators (not tenant-scoped).',
    icon: CodeIcon,
    round: 3,
    live: true,
  },
  {
    title: 'Secret Defaults Audit',
    description: 'Change trail on per-environment secret defaults.',
    icon: KeyIcon,
    round: 3,
    live: true,
  },
  {
    title: 'Import / Export',
    description: 'Unified ZIP envelope for globals + catalogs (scope = admin).',
    icon: SwapHorizIcon,
    round: 3,
    live: true,
  },
  {
    title: 'Editing-Env Map',
    description: 'Per-tenant authoring home env + drift across the promotion ladder.',
    icon: SwapHorizIcon,
    round: 6,
    live: true,
  },
];

/**
 * Round-1 foundation checklist — what actually shipped. Shown as a
 * compact signal at the top of the home page so a super-admin lands
 * on something honest ("what's live?") instead of 6 identical
 * "coming soon" tiles.
 */
const SHIPPED = [
  '/api/me with app_user / app_user_role lookup + email-verified guard',
  'config.app_user, config.app_role, config.app_user_role, admin.admin_action_log',
  'ADMIN_VIEW_ENABLED kill switch (frontend + backend)',
  'Admin shell: env strip, sidebar, top bar, entry points from SelectTenant + UserBlock',
];

const AdminHome: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 1120 }}>
      {/* Compact heading — the env strip already announces Admin View. */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Home</Typography>
        <Typography sx={{ ...typo.pageSubtitle }}>
          Manage global entities, reference catalogs, and platform access.
        </Typography>
      </Box>

      {/* Operator action before the status cards: it is the one thing on this
          page someone comes here to DO, rather than read. */}
      <ResetCacheCard />

      {/* Round-1 status card — single source of truth for what's live. */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckCircleIcon sx={{ color: colors.statusActive, fontSize: 20 }} />
          <Typography
            sx={{
              ...typo.sectionTitle,
              fontSize: 16,
              color: colors.textPrimary,
            }}
          >
            Round 1 foundation is live
          </Typography>
          <Box
            sx={{
              ml: 1,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: colors.statusActive,
              backgroundColor: colors.successBg,
              borderRadius: '4px',
              px: 0.75,
              py: 0.25,
            }}
          >
            Shipped
          </Box>
        </Box>

        <Typography sx={{ ...typo.secondaryText, mb: 2 }}>
          Authentication, app-level roles, audit, and the admin shell are in place. Feature
          surfaces land in subsequent rounds.
        </Typography>

        <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}>
          {SHIPPED.map((line) => (
            <Typography
              key={line}
              component="li"
              sx={{ ...typo.secondaryText, fontSize: 13, lineHeight: 1.6 }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      </Paper>

      {/* Upcoming surfaces — compact list, not a tile grid. */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: colors.textTertiary,
          }}
        >
          Upcoming
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
          overflow: 'hidden',
        }}
      >
        {UPCOMING.map((s, idx) => {
          const Icon = s.icon;
          const isLast = idx === UPCOMING.length - 1;
          return (
            <Box
              key={s.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 1.75,
                borderBottom: isLast ? 'none' : `1px solid ${colors.borderLight}`,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: colors.adminAccentSubtle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: colors.adminAccent, fontSize: 20 }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: colors.textPrimary,
                    lineHeight: 1.3,
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    ...typo.secondaryText,
                    fontSize: 13,
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  {s.description}
                </Typography>
              </Box>

              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'Manrope, sans-serif',
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: s.live
                    ? colors.statusActive
                    : s.round === 2
                      ? colors.adminAccent
                      : colors.textTertiary,
                  backgroundColor: s.live
                    ? colors.successBg
                    : s.round === 2
                      ? colors.adminAccentSubtle
                      : colors.bgMuted,
                  borderRadius: '4px',
                  px: 1,
                  py: 0.25,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {s.live ? 'Live' : `Round ${s.round}`}
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default AdminHome;
